import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Flex,
    Select,
    Spinner,
    Text,
} from '@chakra-ui/react';
import { getErrorMessage } from 'api-v2';
import { getQuoteRevisions } from '../../api/revision-api';
import { formatRevisionLabel } from '../../../components/QuoteTable/utils';
import { ReadOnlyQuoteTable } from './components/ReadOnlyQuoteTable';
import { Modal } from 'newui/components/ui/Modal';
import type { QuoteTableColumn } from '../../../components/QuoteTable/quote-table.types';
import type { QuoteRevisionSnapshot } from '../../types/revision.type';
import type { RfxQuoteItem, RfxQuoteColumn } from '../../types/quote.type';
import { cellDataToLookup, getCellDiff, mapCellSnapshotToRows, mapQuoteTableRowsToReadOnly } from './utils';

const DEFAULT_ERROR_MESSAGE =
    "We couldn't load the revisions for this quote. Please close this window and try again.";

export interface CompareRevisionsModalProps {
    columns?: RfxQuoteColumn[];
    initialRevisionA?: number;
    initialRevisionB?: number;
    inviteId: number;
    isOpen: boolean;
    quoteId: number;
    quoteItems: RfxQuoteItem[];
    quoteName: string;
    onClose: () => void;
}

const CompareRevisionsModal: React.FC<CompareRevisionsModalProps> = ({
    isOpen,
    onClose,
    columns: quoteColumns,
    inviteId,
    quoteId,
    quoteName,
    quoteItems,
    initialRevisionA,
    initialRevisionB,
}) => {
    const tableColumns: QuoteTableColumn[] = (quoteColumns ?? []).map((c) => ({
        id: c.id,
        columnType: c.columnType,
        label: c.name,
        sortOrder: c.sortOrder,
        isDefault: c.isClientEditable,
        isSupplierEditable: c.isSupplierEditable,
    }));
    const [revisions, setRevisions] = useState<QuoteRevisionSnapshot[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedA, setSelectedA] = useState<number | undefined>(initialRevisionA);
    const [selectedB, setSelectedB] = useState<number | undefined>(initialRevisionB);
    const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

    const fetchRevisions = useCallback(async () => {
        if (!isOpen) {
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            const data = await getQuoteRevisions({ inviteId: inviteId, quoteId });
            setRevisions(data);

            const sorted = [...data].sort((a, b) => b.revision - a.revision);
            const maxRevision = sorted[0]?.revision ?? 0;
            const secondMax = sorted[1]?.revision ?? 0;

            setSelectedA(maxRevision);
            setSelectedB(secondMax);
        } catch (e) {
            // A failed fetch previously left `revisions` empty, making it
            // indistinguishable from an RFx that genuinely has none. Prefer the
            // API's own message so the two 422 gates (rfx_invite_id vs rfx_id)
            // stay distinguishable to whoever is reading the screen.
            setRevisions([]);
            setError(getErrorMessage(e) ?? DEFAULT_ERROR_MESSAGE);
        } finally {
            setIsLoading(false);
        }
    }, [isOpen, inviteId, quoteId]);

    useEffect(() => {
        fetchRevisions();
    }, [fetchRevisions]);

    const revisionA = useMemo(
        () => revisions.find((r) => r.revision === selectedA),
        [revisions, selectedA]
    );

    const revisionB = useMemo(
        () => revisions.find((r) => r.revision === selectedB),
        [revisions, selectedB]
    );

    const rowsA = useMemo(() => {
        if (!revisionA) {
            return [];
        }
        return mapCellSnapshotToRows(cellDataToLookup(revisionA.cells ?? []), quoteItems, quoteColumns);
    }, [revisionA, quoteItems, quoteColumns]);

    const rowsB = useMemo(() => {
        if (!revisionB) {
            return [];
        }
        return mapCellSnapshotToRows(cellDataToLookup(revisionB.cells ?? []), quoteItems, quoteColumns);
    }, [revisionB, quoteItems, quoteColumns]);

    const cellDiffKeys = useMemo(() => {
        if (!revisionA || !revisionB) {
            return new Set<string>();
        }
        const cellsA = cellDataToLookup(revisionA.cells ?? []);
        const cellsB = cellDataToLookup(revisionB.cells ?? []);
        const base = getCellDiff(cellsA, cellsB);
        // `total` is computed, not stored — propagate diffs from input
        // columns (quantity / response_value / saving) onto the total
        // column so its cell highlights when the derived value changes.
        const totalCol = quoteColumns?.find((c) => c.columnType === 'total');
        if (!totalCol) {
            return base;
        }
        const inputColIds = new Set(
            (quoteColumns ?? [])
                .filter(
                    (c) =>
                        c.columnType === 'response_quantity' ||
                        c.columnType === 'response_value' ||
                        c.columnType === 'saving'
                )
                .map((c) => c.id)
        );
        const augmented = new Set(base);
        for (const key of base) {
            const m = key.match(/^cell-(\d+)-(\d+)$/);
            if (!m) continue;
            const rowId = Number(m[1]);
            const colId = Number(m[2]);
            if (inputColIds.has(colId)) {
                augmented.add(`cell-${rowId}-${totalCol.id}`);
            }
        }
        return augmented;
    }, [revisionA, revisionB, quoteColumns]);

    const readOnlyRowsA = useMemo(
        () => mapQuoteTableRowsToReadOnly(rowsA, tableColumns),
        [rowsA, tableColumns]
    );

    const readOnlyRowsB = useMemo(
        () => mapQuoteTableRowsToReadOnly(rowsB, tableColumns),
        [rowsB, tableColumns]
    );

    const hasMultipleRevisions = revisions.length >= 3;

    // The comparison grid renders nothing at all unless at least one side
    // resolves, so that — not `revisions.length` — is what separates "loaded but
    // nothing to show" from a real comparison.
    const hasRevisionsToCompare = Boolean(revisionA || revisionB);

    const handleSelectA = (value: number) => {
        if (value === selectedB) {
            const adjacent = value > 0 ? value - 1 : value + 1;
            setSelectedB(adjacent);
        }
        setSelectedA(value);
    };

    const handleSelectB = (value: number) => {
        if (value === selectedA) {
            const adjacent = value > 0 ? value - 1 : value + 1;
            setSelectedA(adjacent);
        }
        setSelectedB(value);
    };

    const labelA = hasMultipleRevisions
        ? formatRevisionLabel(selectedA ?? 0)
        : `${formatRevisionLabel((revisions[1]?.revision) ?? 1)} (Latest)`;

    const labelB = hasMultipleRevisions
        ? formatRevisionLabel(selectedB ?? 0)
        : 'Original';

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="full"
            title="Compare Revisions"
            description={`for '${quoteName}'`}
            modalType="standard"
            iconType="info"
            closeOnEsc
            closeOnOverlayClick
            secondaryButton={{
                onClick: onClose,
                text: "Close",
                size: "sm",
                width: "full"
            }}
            wrapDescription
        >
            {isLoading && (
                <Flex
                    justify="center"
                    align="center"
                    py={8}
                >
                    <Spinner size="lg" />
                </Flex>
            )}

            {!isLoading && error && (
                <Flex
                    justify="center"
                    align="center"
                    direction="column"
                    gap={2}
                    py={8}
                    data-testid="compare-revisions-error"
                >
                    <Text className="text-base font-semibold text-red-600">
                        Couldn&rsquo;t load revisions
                    </Text>
                    <Text className="text-center text-sm text-gray-600">{error}</Text>
                </Flex>
            )}

            {!isLoading && !error && !hasRevisionsToCompare && (
                <Flex
                    justify="center"
                    align="center"
                    py={8}
                    data-testid="compare-revisions-empty"
                >
                    <Text className="text-sm text-gray-600">
                        No revisions to compare yet.
                    </Text>
                </Flex>
            )}

            {!isLoading && !error && hasRevisionsToCompare && (
                <>
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                        {revisionA && (
                            <div>
                                <p className="mb-3 text-base font-semibold text-gray-700">
                                    {hasMultipleRevisions ? <Select
                                        value={selectedA ?? ''}
                                        onChange={(e) => handleSelectA(Number(e.target.value))}
                                        data-testid="compare-select-a"
                                        fontSize={"sm"}
                                        width={150}
                                    >
                                        {revisions.map((r) => (
                                            <option
                                                key={r.revision}
                                                value={r.revision}
                                            >
                                                {formatRevisionLabel(r.revision)}
                                            </option>
                                        ))}
                                    </Select> : labelA}
                                </p>
                                <ReadOnlyQuoteTable
                                    columns={tableColumns}
                                    rows={readOnlyRowsA}
                                    diffKeys={cellDiffKeys}
                                    hoveredRowIndex={hoveredRowIndex}
                                    onRowHover={setHoveredRowIndex}
                                />
                            </div>
                        )}

                        {revisionB && (
                            <div>
                                <p className="mb-3 text-base font-semibold text-gray-500">
                                    {hasMultipleRevisions ? <Select
                                        value={selectedB ?? ''}
                                        onChange={(e) => handleSelectB(Number(e.target.value))}
                                        data-testid="compare-select-b"
                                        fontSize={"sm"}
                                        width={150}
                                    >
                                        {revisions.map((r) => (
                                            <option
                                                key={r.revision}
                                                value={r.revision}
                                            >
                                                {formatRevisionLabel(r.revision)}
                                            </option>
                                        ))}
                                    </Select> : labelB}
                                </p>
                                <ReadOnlyQuoteTable
                                    columns={tableColumns}
                                    rows={readOnlyRowsB}
                                    hoveredRowIndex={hoveredRowIndex}
                                    muted
                                    onRowHover={setHoveredRowIndex}
                                />
                            </div>
                        )}
                    </div>
                </>
            )}
        </Modal>
    );
};

export { CompareRevisionsModal };
